/**
 * Some table headers show a small info icon before their label, with a tooltip attached to that
 * icon alone.
 *
 * The icon cannot be part of the column's `label()`: Filament 4 serialises every column label into
 * the Livewire component state, and rendered HTML does not survive that round trip, which breaks
 * the snapshot checksum. Injecting a real element is no better — Livewire's morph removes anything
 * it did not render when the table refreshes.
 *
 * So the icon is drawn by CSS, and the tooltip is driven from here: Tippy is left on the header
 * cell but triggered manually, and only while the pointer is actually over the icon's box.
 */

/** Matches the icon size and offset used by the admin theme. */
const ICON_SIZE = 20

const getIconRect = (headerCell: HTMLElement): DOMRect => {
    const anchor = headerCell.querySelector('.fi-ta-header-cell-sort-btn') ?? headerCell
    const { left, top, height } = anchor.getBoundingClientRect()

    return new DOMRect(left, top + (height - ICON_SIZE) / 2, ICON_SIZE, ICON_SIZE)
}

/** The box Tippy should point at, rather than the full width of the header cell. */
export const headerIconRect = (headerCell: HTMLElement): DOMRect => getIconRect(headerCell)

/** Whether the pointer is over the icon, so the tooltip only opens there and not on the label. */
export const isPointerOverHeaderIcon = (headerCell: HTMLElement, event: MouseEvent): boolean => {
    const { left, top, right, bottom } = getIconRect(headerCell)

    return (
        event.clientX >= left &&
        event.clientX <= right &&
        event.clientY >= top &&
        event.clientY <= bottom
    )
}
